import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList } from "lucide-react";
import { formatDate } from "@/lib/format";
import type { WorkRequest } from "@db/schema";

interface WorkRequestsResponse {
  data: WorkRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "website_change", label: "Website Change" },
  { value: "tech_support", label: "Tech Support" },
  { value: "creative_design", label: "Creative Design" },
  { value: "new_project", label: "New Project" },
  { value: "other", label: "Other" },
] as const;

const STATUSES = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "ticket_created", label: "Ticket Created" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  website_change: "Website Change",
  tech_support: "Tech Support",
  creative_design: "Creative Design",
  new_project: "New Project",
  other: "Other",
};

export default function WorkRequestsPage() {
  const [, navigate] = useLocation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (search.trim()) queryParams.set("search", search.trim());
  if (categoryFilter !== "all") queryParams.set("category", categoryFilter);
  if (statusFilter !== "all") queryParams.set("status", statusFilter);

  const { data, isLoading } = useQuery<WorkRequestsResponse>({
    queryKey: [`/api/intake/work-requests?${queryParams.toString()}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const columns: ColumnDef<WorkRequest>[] = [
    {
      key: "fullName",
      header: "Name",
      cell: (row) => <span className="font-medium">{row.fullName}</span>,
    },
    {
      key: "company",
      header: "Company",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">{row.company}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      cell: (row) => (
        <span className="text-sm">
          {CATEGORY_LABELS[row.category] ?? row.category}
        </span>
      ),
      className: "w-[140px]",
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="workRequest" status={row.status} />,
      className: "w-[140px]",
    },
    {
      key: "estimate",
      header: "Estimate",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.aiEstimateHours != null ? `${row.aiEstimateHours}h` : "\u2014"}
        </span>
      ),
      className: "w-[100px]",
    },
    {
      key: "createdAt",
      header: "Submitted",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.createdAt)}
        </span>
      ),
      className: "w-[130px]",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Work Requests"
        description="Manage intake work requests from the service request form"
      />

      <DataTable
        columns={columns}
        data={data?.data}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/work-requests/${row.id}`)}
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search by name, email, or company..."
        emptyIcon={ClipboardList}
        emptyTitle="No work requests found"
        emptyDescription="Work requests submitted through the intake form will appear here."
        pagination={
          data?.pagination
            ? {
              page: data.pagination.page,
              totalPages: data.pagination.totalPages,
              onPageChange: setPage,
            }
            : undefined
        }
        filterSlot={
          <div className="flex gap-2">
            <Select
              value={categoryFilter}
              onValueChange={(val) => {
                setCategoryFilter(val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />
    </div>
  );
}
